import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { getAllBranch, getRouteManagementList } from "../../services";
import { getBranchCode } from "../../services/keycloak";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import {
  RouteManagementList,
  RouteManagementRow,
} from "../../types/route.type";
import { Branch } from "../../types/user.type";
import DeleteRouteDialog from "./DeleteRouteDialog";
import QrPaymentDialog from "./QrPaymentDialog";
import RouteNameDialog, { RouteNameDialogMode } from "./RouteNameDialog";
import RouteTable from "./RouteTable";
import TestModeDialog from "./TestModeDialog";
import {
  ROUTE_MENU_TITLE,
  formatRouteSeq,
  pickDefaultBranchUuid,
  summarizeRoutes,
} from "./routeText";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";

const BRANCH_SELECT_WIDTH = 320;

/**
 * สายส่ง — the ordinary routes (01–16) and route 99 of one branch. A branch manager works on
 * their own branch; admin, master and head office pick one. Head office only reads.
 */
export default function RouteManagement() {
  const role = useUserRole();
  const canPickBranch = featureAccess.routeAllBranches(role);
  const canWrite = featureAccess.routeWrite(role);
  const canEditTestMode = featureAccess.routeTestModeEdit(role);
  const canEditQr = featureAccess.routeQrEdit(role);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchUuid, setBranchUuid] = useState<string>("");
  const [routeList, setRouteList] = useState<RouteManagementList | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [nameDialogMode, setNameDialogMode] =
    useState<RouteNameDialogMode | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RouteManagementRow | null>(null);
  const [testModeTarget, setTestModeTarget] =
    useState<RouteManagementRow | null>(null);
  const [qrTarget, setQrTarget] = useState<RouteManagementRow | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const loadBranches = useCallback(async () => {
    if (!canPickBranch) return;
    try {
      const { data } = await getAllBranch();
      const loaded: Branch[] = data.data ?? [];
      setBranches(loaded);
      setBranchUuid(pickDefaultBranchUuid(loaded, getBranchCode()));
    } catch {
      // The interceptor already reported the failure; the picker stays empty.
      setBranches([]);
    }
  }, [canPickBranch]);

  const loadRoutes = useCallback(async () => {
    // An all-branch user sees nothing until a branch is picked; a branch manager's branch
    // comes from the token on the server.
    if (canPickBranch && !branchUuid) return;
    setLoading(true);
    try {
      const { data } = await getRouteManagementList(
        canPickBranch ? branchUuid : undefined,
      );
      setRouteList(data.data);
    } catch {
      // The interceptor already reported the failure; show an empty table, not stale rows.
      setRouteList(null);
    } finally {
      setLoading(false);
    }
  }, [canPickBranch, branchUuid]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadRoutes();
  }, [loadRoutes]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setBranchUuid(event.target.value);
    setRefusal(null);
  };

  const handleOpenCreate = () => {
    setNameDialogMode({
      kind: "create",
      slots: routeList?.available_slots ?? [],
      branchUuid: canPickBranch ? branchUuid : undefined,
    });
  };

  const handleRename = (route: RouteManagementRow) => {
    setNameDialogMode({ kind: "rename", route });
  };

  const handleCloseNameDialog = () => {
    setNameDialogMode(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const handleCloseTestModeDialog = () => {
    setTestModeTarget(null);
  };

  const handleCloseQrDialog = () => {
    setQrTarget(null);
  };

  const handleRefused = (message: string) => {
    setRefusal(message);
    loadRoutes();
  };

  const handleCloseRefusal = () => {
    setRefusal(null);
  };

  const rows = routeList?.rows ?? [];
  const slots = routeList?.available_slots ?? [];
  const summary = summarizeRoutes(rows);
  const hasList = routeList !== null;
  const hasFreeSlot = slots.length > 0;
  const canCreate = hasList && hasFreeSlot;
  const branchLabel = routeList
    ? `${routeList.branch.branch_code} ${routeList.branch.branch_name}`
    : "-";
  const createTooltip =
    hasList && !hasFreeSlot ? "สาขานี้ใช้เลขสายครบ 01–16 แล้ว" : "";

  return (
    <Stack sx={{ py: 1 }}>
      <Stack gap={0.5} mb={1}>
        <Typography variant="h6">{ROUTE_MENU_TITLE}</Typography>
        <Typography variant="body2" color="text.secondary">
          สายปกติของแต่ละสาขาใช้เลข 01–16 ส่วนสาย 99 (สายพิเศษ)
          ระบบสร้างให้และแก้ไขจากหน้านี้ไม่ได้
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack gap={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            {canPickBranch ? (
              <TextField
                select
                size="small"
                label="สาขา"
                value={branchUuid}
                onChange={handleChangeBranch}
                sx={{ width: BRANCH_SELECT_WIDTH, maxWidth: "100%" }}
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.uuid} value={branch.uuid}>
                    {branch.branch_code} — {branch.branch_name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body1" fontWeight={600}>
                สาขา {branchLabel}
              </Typography>
            )}
            {canWrite && (
              <Tooltip title={createTooltip}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={!canCreate}
                    onClick={handleOpenCreate}
                  >
                    สร้างสายส่ง
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>

          {hasList && (
            <>
              <Typography variant="body2" color="text.secondary">
                สายปกติ {summary.mainCount} สาย (อยู่ในโหมดทดสอบ{" "}
                {summary.testModeCount} สาย) · สายพิเศษ {summary.specialCount}{" "}
                สาย
              </Typography>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" fontWeight={600}>
                  เลขสายที่ว่าง:
                </Typography>
                {slots.map((slot) => (
                  <Chip
                    key={slot.route_seq}
                    size="small"
                    variant="outlined"
                    label={formatRouteSeq(slot.route_seq)}
                  />
                ))}
                {!hasFreeSlot && (
                  <Typography variant="body2" color="text.secondary">
                    ไม่มี (ใช้ครบ 01–16 แล้ว)
                  </Typography>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      {refusal && (
        <Alert severity="warning" onClose={handleCloseRefusal} sx={{ mb: 2 }}>
          {refusal}
        </Alert>
      )}

      <RouteTable
        routes={rows}
        loading={loading}
        canWrite={canWrite}
        canEditTestMode={canEditTestMode}
        canEditQr={canEditQr}
        onRename={handleRename}
        onToggleTestMode={setTestModeTarget}
        onToggleQr={setQrTarget}
        onDelete={setDeleteTarget}
      />

      <RouteNameDialog
        mode={nameDialogMode}
        onClose={handleCloseNameDialog}
        onSaved={loadRoutes}
      />
      <DeleteRouteDialog
        target={deleteTarget}
        onClose={handleCloseDeleteDialog}
        onDeleted={loadRoutes}
        onRefused={handleRefused}
      />
      <TestModeDialog
        target={testModeTarget}
        onClose={handleCloseTestModeDialog}
        onSaved={loadRoutes}
      />
      <QrPaymentDialog
        target={qrTarget}
        onClose={handleCloseQrDialog}
        onSaved={loadRoutes}
      />
    </Stack>
  );
}
